import { Link } from 'react-router-dom';

/**
 * A crumb. A plain string is a label; an object with `to` navigates.
 *
 * The breadcrumb IS the back-navigation (reference §6), which is why detail pages carry no
 * redundant "Back" button — so the ancestor crumbs of a detail page have to be links.
 */
export type BreadcrumbSegment = string | { label: string; to: string };

/**
 * The breadcrumb strip that tops every index and detail page — reference §3 and §7.
 *
 * Uniform weight throughout — the current page is distinguished by colour alone, never by
 * bold. The last crumb is where the user already is, so it never links even if a `to` is
 * supplied.
 *
 * **It begins at the first crumb that NAVIGATES.** A caller still passes the whole path, suite
 * and group included; the strip drops the leading labels itself, so most pages render as their
 * own name and a detail page as the chain that can actually be walked back. See below.
 */
export function Breadcrumb({ segments }: { segments: BreadcrumbSegment[] }) {
  /*
    LEADING crumbs that go nowhere are DROPPED (product call, 26 Oct 2026).

    The breadcrumb IS the back-navigation, so a crumb that cannot be clicked is doing no
    navigating — and the ones at the front are the suite and the nav group, which are labels
    rather than places: there is no `/human-resources` page to return to. `Human Resources ›
    Recruitment › Applicants › SAMPLE-UH286E-001 › Applicant UIBR46` spent two of its five
    crumbs on words the sidebar is already showing, and pushed the part that navigates right.

    **LEADING only, and the LAST is always kept.** A dead crumb in the MIDDLE still marks a
    real step in the path, and the last one is the page the reader is on — `aria-current`
    hangs off it, so dropping it would leave the strip saying nothing about where they are.
    Most pages pass `['Suite', 'Group', 'Page']` with no links at all, and those correctly
    come down to the page's own name.

    Done HERE rather than at 76 call sites: a rule spelled out per page is one that drifts on
    the page nobody revisits.
  */
  let start = 0;
  while (start < segments.length - 1 && typeof segments[start] === 'string') {
    start += 1;
  }

  const visible = segments.slice(start);

  return (
    <nav aria-label="Breadcrumb" className="shrink-0 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm">
        {visible.map((segment, index) => {
          const label = typeof segment === 'string' ? segment : segment.label;
          const to = typeof segment === 'string' ? null : segment.to;
          const isCurrent = index === visible.length - 1;

          return (
            <li key={label} className="flex items-center gap-1.5">
              {index > 0 ? (
                <span className="text-slate-300" aria-hidden="true">
                  ›
                </span>
              ) : null}

              {to && !isCurrent ? (
                <Link
                  to={to}
                  className="rounded text-slate-600 transition hover:text-slate-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                >
                  {label}
                </Link>
              ) : (
                <span
                  className={isCurrent ? 'text-slate-800' : 'text-slate-600'}
                  aria-current={isCurrent ? 'page' : undefined}
                >
                  {label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
